import React, { useState, useMemo, useCallback } from 'react';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import { apiFetch } from '@/lib/api';
import {
  LEGACY_KEYS,
  readLegacyData,
  clearLegacyData,
  isMigrationDone,
  markMigrationDone,
  getImportedLegacyIds,
  addImportedLegacyIds,
} from '@/utils/localStorage';
import {
  FiDatabase,
  FiUploadCloud,
  FiTrash2,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
} from 'react-icons/fi';

/**
 * DatenUebernahme
 *
 * Einmalige Bruecke von der localStorage-Zeit in den Server-Betrieb.
 * Findet Altdaten im Browser, zeigt sie ehrlich an und laesst den Nutzer
 * entscheiden, was auf den Server wandert und was geloescht wird.
 *
 * Bewusste Grenzen:
 *  - Es wird nie automatisch etwas geloescht oder abgewaehlt-uebernommen.
 *  - Uebernommen werden nur Leads (POST /api/leads). Alle uebrigen Altlasten
 *    (Kalkulationen, geteilte Angebote, Versorger, Preise, Aufschlags- und
 *    Margen-Einstellungen) werden nur gezaehlt, angezeigt und koennen
 *    geloescht werden.
 *  - Ohne Altdaten rendert die Komponente null.
 */

// Bekannte Demo-Datensaetze aus der Zeit, als Demo-Leads automatisch
// eingespielt wurden. Firmenname UND Verbrauch muessen passen — ein echter
// Kunde koennte zufaellig "Mueller GmbH" heissen.
const DEMO_LEADS = [
  { name: 'mueller gmbh', verbrauch: 250000 },
  { name: 'schmidt metallbau', verbrauch: 500000 },
  { name: 'weber kunststoffe', verbrauch: 120000 },
];

// Status-Werte, die das Backend kennt. Alles andere faellt auf 'neu' zurueck,
// damit ein kaputter Altdatensatz die Uebernahme nicht scheitern laesst.
const VALID_STATUSES = [
  'neu', 'qualifiziert', 'angebot_erstellt', 'angebot_gesendet', 'angebot_geoeffnet',
  'in_verhandlung', 'angenommen', 'vertrag', 'verloren',
];

const VALID_PRIORITIES = ['niedrig', 'normal', 'hoch', 'dringend'];

const KEY_LABELS = {
  [LEGACY_KEYS.leads]: 'Leads',
  [LEGACY_KEYS.kalkulationen]: 'Kalkulationen',
  [LEGACY_KEYS.sharedKalkulationen]: 'Geteilte Angebote',
  [LEGACY_KEYS.suppliers]: 'Versorger',
  [LEGACY_KEYS.supplierPrices]: 'Versorgerpreise',
  [LEGACY_KEYS.offers]: 'Angebote',
  [LEGACY_KEYS.offerEvents]: 'Angebots-Ereignisse',
  [LEGACY_KEYS.marginSettings]: 'Margen-Einstellungen',
  [LEGACY_KEYS.strukturaufschlag]: 'Strukturaufschlag',
  [LEGACY_KEYS.settings]: 'Sonstige Einstellungen',
};

// Die vier Schluessel mit eigener Kachel. Alles Weitere wird als Liste
// nachgereicht, damit die Karte nicht zur Schluesseltabelle wird.
const HEADLINE_KEYS = [
  LEGACY_KEYS.leads,
  LEGACY_KEYS.kalkulationen,
  LEGACY_KEYS.suppliers,
  LEGACY_KEYS.supplierPrices,
];

const normName = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Zahl oder null — niemals '' und niemals NaN.
 * Beherrscht die deutsche Schreibweise aus Altdaten ('1.234,5' -> 1234.5).
 * Ein Punkt allein bleibt Dezimalpunkt, damit '12.5' nicht zu 125 wird.
 */
const toNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return null;

  const text = String(value).trim();
  if (text === '') return null;

  const normalized = text.includes(',')
    ? text.replace(/\./g, '').replace(',', '.')
    : text;

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

/** Text oder null — kein leerer String. Fuer optionale Felder. */
const toTextOrNull = (value) => {
  const text = String(value ?? '').trim();
  return text === '' ? null : text;
};

/**
 * Immer ein String, notfalls leer. Fuer Spalten, die NOT NULL sind
 * (contacts.first_name / contacts.last_name) — dort waere null ein Fehler,
 * der den ganzen Datensatz verlieren wuerde.
 */
const toText = (value) => String(value ?? '').trim();

/**
 * Datum als 'YYYY-MM-DD' oder null. Ein leeres oder unverstaendliches
 * Datum wird zu null — niemals zu '', das eine DATE-Spalte ablehnt.
 * Zeitstempel aus Altdaten ('2024-05-01T10:00:00Z') werden auf den Tag gekuerzt.
 */
const toDateOrNull = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }

  const text = toText(value);
  if (text === '') return null;

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // Deutsche Schreibweise aus alten Formularen: 01.05.2024
  const de = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (de) return `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;

  // Alles andere lieber weglassen als ein falsches Datum anlegen.
  return null;
};

/** Wert aus einer Auswahlliste, sonst der Rueckfallwert. */
const pickEnum = (value, allowed, fallback) => {
  const text = String(value ?? '').trim().toLowerCase();
  return allowed.includes(text) ? text : fallback;
};

/**
 * Demo-Erkennung: nur wenn Firmenname (exakt, Gross-/Kleinschreibung egal)
 * UND Jahresverbrauch zusammenpassen.
 */
const isDemoLead = (name, verbrauch) => {
  const n = normName(name);
  const v = toNumberOrNull(verbrauch);
  if (!n || v === null) return false;
  return DEMO_LEADS.some((demo) => demo.name === n && demo.verbrauch === v);
};

/**
 * Altes Lead-Format (verschiedene Generationen) auf eine Zeile normalisieren.
 * Aeltere Datensaetze lagen teils flach vor, neuere mit company/contact-Objekt.
 */
const normalizeLegacyLead = (raw, index) => {
  const company = raw?.company && typeof raw.company === 'object' ? raw.company : {};
  const contact = raw?.contact && typeof raw.contact === 'object' ? raw.contact : {};

  const name = company.name ?? raw?.company_name ?? raw?.firma ?? '';
  const verbrauch = company.annual_consumption_kwh ?? raw?.annual_consumption_kwh ?? raw?.verbrauch ?? null;

  const firstName = contact.first_name ?? raw?.contact_first_name ?? '';
  const lastName = contact.last_name ?? raw?.contact_last_name ?? '';

  const legacyId = raw?.id !== undefined && raw?.id !== null ? String(raw.id) : '';
  // Fingerabdruck fuer die Doppel-Uebernahme-Sperre. Faellt auf Inhalt zurueck,
  // wenn der Altdatensatz keine ID hatte.
  const fingerprint = legacyId || `${normName(name)}|${toNumberOrNull(verbrauch) ?? ''}|${raw?.created_at ?? index}`;

  return {
    rowKey: `${index}-${fingerprint}`,
    index,
    legacyId,
    fingerprint,
    raw,
    name: String(name || '').trim(),
    verbrauch: toNumberOrNull(verbrauch),
    contactName: `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim(),
    plz: String(company.plz ?? raw?.plz ?? '').trim(),
    city: String(company.city ?? raw?.city ?? '').trim(),
    status: String(raw?.status ?? '').trim(),
    isDemo: isDemoLead(name, verbrauch),
  };
};

/**
 * Alte Lead-Zeile auf den Body von POST /api/leads abbilden.
 *
 * Regeln, damit kein Datensatz an einer Kleinigkeit scheitert:
 *  - first_name/last_name sind in der Datenbank NOT NULL -> immer String,
 *    notfalls leer.
 *  - Zahlenfelder sind Zahl oder null, nie '' und nie NaN.
 *  - Datumsfelder sind 'YYYY-MM-DD' oder null, nie ''.
 *  - Alle uebrigen optionalen Textfelder sind Text oder null, nie ''.
 */
const toApiPayload = (row) => {
  const raw = row.raw || {};
  const company = raw.company && typeof raw.company === 'object' ? raw.company : {};
  const contact = raw.contact && typeof raw.contact === 'object' ? raw.contact : {};

  return {
    company: {
      // Der Server weist einen leeren Firmennamen ab. Ein Platzhalter ist
      // ehrlicher als ein verlorener Datensatz — die Zeile wird in der Liste
      // ebenfalls als "Ohne Firmennamen" angezeigt.
      name: row.name || 'Unbenannt',
      street: toTextOrNull(company.street ?? raw.street),
      plz: toTextOrNull(company.plz ?? raw.plz),
      city: toTextOrNull(company.city ?? raw.city),
      industry: toTextOrNull(company.industry ?? raw.industry ?? raw.branche),
      // row.verbrauch ist bereits Zahl oder null (normalizeLegacyLead).
      annual_consumption_kwh: row.verbrauch,
      current_price_ct_kwh: toNumberOrNull(
        company.current_price_ct_kwh ?? raw.current_price_ct_kwh ?? raw.aktueller_preis
      ),
      current_supplier: toTextOrNull(company.current_supplier ?? raw.current_supplier ?? raw.versorger),
    },
    contact: {
      first_name: toText(contact.first_name ?? raw.contact_first_name ?? raw.vorname),
      last_name: toText(contact.last_name ?? raw.contact_last_name ?? raw.nachname),
      email: toTextOrNull(contact.email ?? raw.email),
      phone: toTextOrNull(contact.phone ?? raw.phone ?? raw.telefon),
      position: toTextOrNull(contact.position ?? raw.position),
    },
    status: pickEnum(raw.status, VALID_STATUSES, 'neu'),
    priority: pickEnum(raw.priority, VALID_PRIORITIES, 'normal'),
    source: toTextOrNull(raw.source) || 'Sonstiges',
    notes: toTextOrNull(raw.notes),
    next_action: toTextOrNull(raw.next_action),
    next_action_date: toDateOrNull(raw.next_action_date),
  };
};

const formatKwh = (value) =>
  value === null || value === undefined ? '-' : `${Number(value).toLocaleString('de-DE')} kWh`;

const CountBadge = ({ label, count, hint }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{count}</p>
    {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

/**
 * Einmalige Bestandsaufnahme des localStorage. Laeuft genau einmal beim
 * ersten Rendern (lazy useState-Initializer) — danach kommen die Daten nur
 * noch aus dem State.
 */
const buildInitialModel = () => {
  const empty = { legacy: null, rows: [], selected: new Set(), importedKeys: new Set() };

  // Schon uebernommen? Dann nie wieder anzeigen — sonst legt jemand
  // dieselben Leads ein zweites Mal auf dem Server an.
  if (isMigrationDone()) return empty;

  const data = readLegacyData();
  if (!data.hasLegacyData) return empty;
  if (data.total === 0 && data.corruptKeys.length === 0) return empty;

  const alreadyImported = new Set(getImportedLegacyIds());
  const rows = data.leads.map(normalizeLegacyLead);

  // Vorauswahl: echte Eintraege an, Demo-Eintraege aus, bereits uebernommene
  // aus. Der Nutzer kann jede Zeile umstellen.
  const selected = new Set(
    rows
      .filter((row) => !row.isDemo && !alreadyImported.has(row.fingerprint))
      .map((row) => row.rowKey)
  );

  const importedKeys = new Set(
    rows.filter((row) => alreadyImported.has(row.fingerprint)).map((row) => row.rowKey)
  );

  return { legacy: data, rows, selected, importedKeys };
};

const DatenUebernahme = ({ onImported, className = '' }) => {
  const [initial] = useState(buildInitialModel);
  const [legacy, setLegacy] = useState(initial.legacy);
  const [rows, setRows] = useState(initial.rows);
  const [selected, setSelected] = useState(initial.selected);
  const [importedKeys, setImportedKeys] = useState(initial.importedKeys);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [errors, setErrors] = useState([]);
  const [cleared, setCleared] = useState(null);

  const toggleRow = useCallback((rowKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const selectableRows = useMemo(
    () => rows.filter((row) => !importedKeys.has(row.rowKey)),
    [rows, importedKeys]
  );

  const selectedRows = useMemo(
    () => selectableRows.filter((row) => selected.has(row.rowKey)),
    [selectableRows, selected]
  );

  const demoCount = useMemo(() => rows.filter((row) => row.isDemo).length, [rows]);

  const setAll = useCallback((value) => {
    setSelected(value ? new Set(selectableRows.map((row) => row.rowKey)) : new Set());
  }, [selectableRows]);

  const handleImport = async () => {
    if (running || selectedRows.length === 0) return;

    setRunning(true);
    setSummary(null);
    setErrors([]);

    const okKeys = [];
    const okFingerprints = [];
    const failures = [];

    for (let i = 0; i < selectedRows.length; i++) {
      const row = selectedRows[i];
      setProgress({ current: i + 1, total: selectedRows.length, name: row.name || 'Unbenannt' });

      try {
        const resp = await apiFetch('/api/leads', {
          method: 'POST',
          body: JSON.stringify(toApiPayload(row)),
        });

        if (!resp.ok) {
          let message = `HTTP ${resp.status}`;
          try {
            const data = await resp.json();
            if (data?.error) message = data.error;
          } catch {
            // Antwort war kein JSON — HTTP-Status bleibt die Meldung.
          }
          failures.push({ rowKey: row.rowKey, name: row.name || 'Unbenannt', message });
          continue;
        }

        okKeys.push(row.rowKey);
        okFingerprints.push(row.fingerprint);
      } catch (err) {
        failures.push({
          rowKey: row.rowKey,
          name: row.name || 'Unbenannt',
          message: `Server nicht erreichbar: ${err.message}`,
        });
      }
    }

    setProgress(null);

    if (okFingerprints.length > 0) {
      // Erfolgreiche Datensaetze merken, damit ein zweiter Versuch der
      // fehlgeschlagenen Zeilen keine Dubletten anlegt.
      addImportedLegacyIds(okFingerprints);
      setImportedKeys((prev) => new Set([...prev, ...okKeys]));
      setSelected((prev) => {
        const next = new Set(prev);
        okKeys.forEach((key) => next.delete(key));
        return next;
      });
    }

    setSummary({ imported: okKeys.length, failed: failures.length });
    setErrors(failures);

    // Fertig-Marker nur, wenn nichts Offenes mehr uebrig ist. Wer bewusst nur
    // einen Teil uebernimmt, muss die Karte beim naechsten Laden wiederfinden —
    // sonst waeren die restlichen Datensaetze unerreichbar.
    const alleErledigt = failures.length === 0 && okKeys.length >= selectableRows.length;
    if (okKeys.length > 0 && alleErledigt) {
      markMigrationDone(okKeys.length);
    }

    setRunning(false);

    if (typeof onImported === 'function' && okKeys.length > 0) {
      try { onImported(okKeys.length); } catch { /* Aufrufer-Fehler nicht weiterreichen */ }
    }
  };

  const handleClear = () => {
    if (!legacy) return;

    const lines = legacy.presentKeys.map((key) => {
      const label = KEY_LABELS[key] || key;
      const count = legacy.countsByKey?.[key] ?? 0;
      return `- ${key} (${label}: ${count} Eintraege)`;
    });

    // Ehrlich benennen, was dabei unwiederbringlich verloren geht.
    const openLeadCount = selectableRows.length;
    const openHint = openLeadCount > 0
      ? `${openLeadCount} noch nicht uebernommene Leads gehen dabei verloren.\n`
      : '';

    const message =
      'Folgende Daten werden aus diesem Browser geloescht:\n\n' +
      lines.join('\n') +
      '\n\nDas laesst sich nicht rueckgaengig machen. Nicht uebernommene Eintraege sind danach weg.\n' +
      openHint +
      'Uebernommen werden koennen ohnehin nur Leads — alles andere (Kalkulationen, Versorger,\n' +
      'Preise, Einstellungen) wird beim Loeschen ersatzlos entfernt.\n' +
      'Daten auf dem Server sind nicht betroffen.\n\nWirklich loeschen?';

    if (!window.confirm(message)) return;

    const result = clearLegacyData(legacy.presentKeys);
    setCleared(result);

    // Nur das aus der Anzeige nehmen, was wirklich geloescht wurde. Bleibt ein
    // Schluessel stehen (Fehler beim Entfernen), muss der Nutzer das sehen.
    const stillPresent = legacy.presentKeys.filter((key) => !result.removed.includes(key));
    const countsByKey = { ...(legacy.countsByKey || {}) };
    result.removed.forEach((key) => { countsByKey[key] = 0; });
    const total = Object.values(countsByKey).reduce((sum, n) => sum + (Number(n) || 0), 0);

    if (result.removed.includes(LEGACY_KEYS.leads)) {
      setRows([]);
      setSelected(new Set());
    }

    setLegacy({
      ...legacy,
      presentKeys: stillPresent,
      corruptKeys: legacy.corruptKeys.filter((key) => !result.removed.includes(key)),
      countsByKey,
      total,
    });
  };

  // Keine Altdaten (oder Uebernahme bereits erledigt): nichts anzeigen.
  if (!legacy) return null;

  const openLeads = selectableRows.length;
  const countFor = (key) => legacy.countsByKey?.[key] ?? 0;
  // Alles jenseits der vier Kacheln — z.B. geteilte Angebote oder alte
  // Aufschlags-Einstellungen. Nur anzeigen, was wirklich im Browser liegt.
  const extraKeys = legacy.presentKeys.filter((key) => !HEADLINE_KEYS.includes(key));

  return (
    <Card className={className}>
      <div className="flex items-center space-x-2 mb-2">
        <FiDatabase className="text-primary-600" size={20} />
        <Card.Title>Alte Browserdaten gefunden</Card.Title>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        In diesem Browser liegen noch Daten aus der Zeit vor der Server-Umstellung. Sie sind nur
        auf diesem Geraet sichtbar. Uebernehmen Sie die Eintraege, die Sie behalten wollen — danach
        arbeiten alle Kollegen auf demselben Datenbestand.
      </p>

      {/* Gefundene Mengen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {HEADLINE_KEYS.map((key) => (
          <CountBadge
            key={key}
            label={KEY_LABELS[key]}
            count={countFor(key)}
            hint={
              key === LEGACY_KEYS.leads
                ? (demoCount > 0 ? `${demoCount} davon Demo` : null)
                : 'wird nicht uebernommen'
            }
          />
        ))}
      </div>

      {extraKeys.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mb-4">
          <p className="text-xs text-gray-500 mb-1">
            Weitere Altlasten in diesem Browser. Sie werden nicht uebernommen und lassen sich nur
            loeschen:
          </p>
          <ul className="text-xs text-gray-600 space-y-0.5">
            {extraKeys.map((key) => {
              const count = countFor(key);
              return (
                <li key={key}>
                  {KEY_LABELS[key] || key}: {count} {count === 1 ? 'Eintrag' : 'Eintraege'}
                  <span className="text-gray-400"> ({key})</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {legacy.corruptKeys.length > 0 && (
        <div className="flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-4">
          <FiAlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={16} />
          <p className="text-sm text-amber-800">
            Unlesbarer Inhalt in: {legacy.corruptKeys.join(', ')}. Diese Eintraege koennen nicht
            uebernommen, aber geloescht werden.
          </p>
        </div>
      )}

      {/* Lead-Liste */}
      {rows.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              Leads auswaehlen ({selectedRows.length} von {openLeads} ausgewaehlt)
            </p>
            {openLeads > 0 && (
              <div className="space-x-3 text-sm">
                <button type="button" onClick={() => setAll(true)} className="text-primary-600 hover:underline">
                  Alle
                </button>
                <button type="button" onClick={() => setAll(false)} className="text-primary-600 hover:underline">
                  Keine
                </button>
              </div>
            )}
          </div>

          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 max-h-80 overflow-y-auto">
            {rows.map((row) => {
              const alreadyImported = importedKeys.has(row.rowKey);
              const failure = errors.find((e) => String(e.rowKey) === String(row.rowKey));
              // rowKey kann aus Altdaten stammen und Sonderzeichen enthalten —
              // fuer die DOM-ID bereinigen (Index-Praefix haelt sie eindeutig).
              const domId = `uebernahme-${row.rowKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
              return (
                <li key={row.rowKey} className="flex items-start gap-3 px-3 py-2.5">
                  <input
                    type="checkbox"
                    id={domId}
                    checked={selected.has(row.rowKey)}
                    disabled={alreadyImported || running}
                    onChange={() => toggleRow(row.rowKey)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <label htmlFor={domId} className="flex-1 min-w-0 cursor-pointer">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{row.name || 'Ohne Firmennamen'}</span>
                      {row.isDemo && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title="Frueher automatisch angelegte Beispielfirma, kein echter Kunde. Deshalb nicht vorausgewaehlt — Sie koennen den Haken aber setzen."
                        >
                          Demo-Datensatz
                        </span>
                      )}
                      {alreadyImported && (
                        <span
                          className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                          title="Dieser Eintrag liegt bereits auf dem Server. Ein zweites Mal uebernehmen wuerde eine Dublette anlegen."
                        >
                          bereits uebernommen
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {[row.contactName, formatKwh(row.verbrauch), [row.plz, row.city].filter(Boolean).join(' ')]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {failure && (
                      <span className="block text-xs text-red-600 mt-0.5">Fehler: {failure.message}</span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>

          {demoCount > 0 && (
            <div className="flex items-start space-x-2 mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <FiInfo className="text-gray-400 mt-0.5 shrink-0" size={14} />
              <div className="text-xs text-gray-500 space-y-1">
                <p>
                  <strong className="text-gray-700">Was heisst &bdquo;Demo-Datensatz&ldquo;?</strong>{' '}
                  Fruehere Versionen der Plattform haben beim ersten Start automatisch
                  {' '}{DEMO_LEADS.length} Beispielfirmen angelegt, damit die Oberflaeche nicht leer
                  wirkt. Diese Eintraege stammen nicht von Ihnen und gehoeren zu keinem echten Kunden
                  — deshalb sind sie hier <strong className="text-gray-700">nicht vorausgewaehlt</strong>{' '}
                  und wandern ohne Ihr Zutun nicht auf den Server.
                </p>
                <p>
                  Als Demo gilt eine Zeile nur, wenn Firmenname <em>und</em> Jahresverbrauch exakt
                  einem dieser Beispiele entsprechen. Sollte ein echter Kunde zufaellig so heissen,
                  setzen Sie einfach den Haken — dann wird er ganz normal uebernommen.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fortschritt */}
      {running && progress && (
        <p className="text-sm text-gray-500 mb-3">
          Uebernehme {progress.current} von {progress.total}: {progress.name} ...
        </p>
      )}

      {/* Ergebnis */}
      {summary && (
        <div
          className={`flex items-start space-x-2 rounded-lg border px-3 py-2 mb-3 ${
            summary.failed > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
          }`}
        >
          {summary.failed > 0 ? (
            <FiAlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={16} />
          ) : (
            <FiCheckCircle className="text-emerald-600 mt-0.5 shrink-0" size={16} />
          )}
          <div className="text-sm">
            <p className={summary.failed > 0 ? 'text-amber-800' : 'text-emerald-800'}>
              {summary.imported} von {summary.imported + summary.failed} Leads auf den Server uebernommen
              {summary.failed > 0 ? `, ${summary.failed} fehlgeschlagen.` : '.'}
            </p>
            {summary.failed === 0 && summary.imported > 0 && (
              <p className="text-emerald-700 mt-0.5">
                Diese Karte erscheint nicht mehr. Die Altdaten liegen weiterhin im Browser — Sie
                koennen sie jetzt loeschen.
              </p>
            )}
            {summary.failed > 0 && (
              <p className="text-amber-700 mt-0.5">
                Die fehlgeschlagenen Zeilen bleiben ausgewaehlt und koennen erneut versucht werden.
                Bereits uebernommene Zeilen sind gesperrt.
              </p>
            )}
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <ul className="mb-3 space-y-1">
          {errors.map((err) => (
            <li key={err.rowKey} className="flex items-start space-x-2 text-sm text-red-600">
              <FiXCircle className="mt-0.5 shrink-0" size={14} />
              <span>
                <strong>{err.name}</strong>: {err.message}
              </span>
            </li>
          ))}
        </ul>
      )}

      {cleared && (
        <div className="flex items-start space-x-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mb-3">
          <FiCheckCircle className="text-gray-500 mt-0.5 shrink-0" size={16} />
          <p className="text-sm text-gray-700">
            Geloescht: {cleared.removed.length > 0 ? cleared.removed.join(', ') : 'nichts'}.
            {cleared.skipped.length > 0 && ` Nicht geloescht: ${cleared.skipped.join(', ')}.`}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-400">
          Uebernommen werden nur Leads. Kalkulationen, Versorgerpreise und Einstellungen legen Sie
          bitte direkt in der Plattform neu an.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={FiTrash2} onClick={handleClear} disabled={running || legacy.presentKeys.length === 0}>
            Alte Browserdaten loeschen
          </Button>
          <Button
            icon={FiUploadCloud}
            onClick={handleImport}
            loading={running}
            disabled={running || selectedRows.length === 0}
          >
            Ausgewaehlte uebernehmen ({selectedRows.length})
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default DatenUebernahme;
